import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Search, Bell, Plus, Pin, Calendar, Trash2, AlertCircle, Clock, CheckCircle, Users, Pencil, Loader2, ArrowLeft, ChevronRight, MoreVertical } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { announcementsAPI } from '../services/api';
import { toast } from '../hooks/use-toast';
import { cn } from '../lib/utils';
import TeamGate from '../components/TeamGate';

const Notes = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isCoach = user?.role === 'coach' || user?.role === 'admin';

  const [searchQuery, setSearchQuery] = useState('');
  const [filterPriority, setFilterPriority] = useState('all');
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);

  // State for Create/Edit
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null); // null if creating, object if editing
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    body: '',
    priority: 'normal',
    due_at: '',
    is_pinned: false,
    show_in_calendar: false,
    target_unit: 'all'
  });

  useEffect(() => {
    loadAnnouncements();
  }, []);

  const loadAnnouncements = async () => {
    try {
      setLoading(true);
      const response = await announcementsAPI.list();
      setAnnouncements(response.data);
    } catch (error) {
      console.error('Failed to load announcements:', error);
      // Use mock data locally if API fails (for now/demo)
      setAnnouncements([]);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingAnnouncement(null);
    setFormData({
      title: '',
      body: '',
      priority: 'normal',
      due_at: '',
      is_pinned: false,
      show_in_calendar: false,
      target_unit: 'all'
    });
    setShowCreateForm(true);
  };

  const openEditModal = (announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      body: announcement.body || '',
      priority: announcement.priority || 'normal',
      due_at: announcement.due_at ? new Date(announcement.due_at).toISOString().split('T')[0] : '', // Simple date format
      is_pinned: announcement.is_pinned || false,
      show_in_calendar: announcement.show_in_calendar || false,
      target_unit: announcement.target_unit || 'all'
    });
    setShowCreateForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast({ title: t('announcements.errorTitleRequired'), variant: 'destructive' });
      return;
    }

    if (formData.show_in_calendar && !formData.due_at) {
      toast({
        title: "Date Required",
        description: "Please select a date if you want this announcement to show in the calendar.",
        variant: 'destructive'
      });
      return;
    }

    try {
      setSubmitting(true);
      // Prepare payload
      const payload = {
        ...formData,
        due_at: formData.due_at ? new Date(formData.due_at).toISOString() : null
      };

      if (editingAnnouncement) {
        // Update existing
        // Assuming API has an update endpoint? Usually PUT /announcements/:id
        // Check api.js service or generic REST pattern.
        // If announcementsAPI doesn't have update, we might need to verify.
        // Assuming standard CRUD.
        await announcementsAPI.update(editingAnnouncement.id, payload);
        toast({ title: "Announcement updated successfully" });
      } else {
        // Create new
        await announcementsAPI.create(payload);
        toast({ title: t('announcements.successCreate') });
      }

      setShowCreateForm(false);
      loadAnnouncements();
    } catch (error) {
      console.error('Failed to save announcement:', error);
      toast({ title: t('announcements.errorCreate'), variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('announcements.deleteConfirm'))) return;

    try {
      await announcementsAPI.delete(id);
      loadAnnouncements();
      toast({ title: t('announcements.successDelete') });
    } catch (error) {
      console.error('Failed to delete announcement:', error);
      toast({ title: t('announcements.errorDelete'), variant: 'destructive' });
    }
  };

  const filteredAnnouncements = announcements.filter(ann => {
    const matchesSearch =
      ann.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (ann.body && ann.body.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesPriority = filterPriority === 'all' || ann.priority === filterPriority;
    return matchesSearch && matchesPriority;
  });

  const priorityConfig = {
    urgent: { color: 'bg-red-600', icon: AlertCircle, label: t('announcements.priorities.urgent') },
    high: { color: 'bg-orange-600', icon: AlertCircle, label: t('announcements.priorities.high') },
    normal: { color: 'bg-blue-600', icon: Bell, label: t('announcements.priorities.normal') },
    low: { color: 'bg-gray-600', icon: CheckCircle, label: t('announcements.priorities.low') }
  };

  return (
    <TeamGate title={t('announcements.title')}>
      <div className="min-h-screen bg-[#0a0a0b] text-white pb-24 animate-in fade-in duration-500">
        {/* Mobile Sticky Header */}
        <div className="lg:hidden p-4 bg-[#0a0a0b]/80 backdrop-blur-xl sticky top-0 z-[100] border-b border-white/5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-white/10 rounded-full transition-colors active:scale-90"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h2 className="text-xl font-black text-white tracking-tight uppercase">
              {t('announcements.title')}
            </h2>
          </div>
          <div className="px-3 py-1 bg-snapp-purple/10 border border-snapp-purple/20 rounded-full">
            <span className="text-[10px] font-black text-snapp-purple uppercase tracking-widest">{announcements.length} {t('announcements.total', 'Duyuru')}</span>
          </div>
        </div>

        <div className="p-4 lg:p-8 space-y-8">
          {/* Header & Main Search */}
          <div className="space-y-6">
            <div className="hidden lg:flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-black text-white uppercase tracking-tight">{t('announcements.title')}</h1>
                <p className="text-gray-400 font-medium mt-1">{t('announcements.subtitle')}</p>
              </div>
              {isCoach && (
                <button
                  onClick={openCreateModal}
                  className="bg-snapp-blue hover:bg-snapp-blue/90 text-white px-6 py-2.5 rounded-2xl font-black text-xs uppercase tracking-wider transition-all active:scale-95 shadow-lg shadow-blue-500/20 flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" /> {t('announcements.new')}
                </button>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative group">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 group-focus-within:text-snapp-purple transition-colors" />
                <input
                  type="text"
                  placeholder={t('announcements.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-[#1a1a1b] border border-white/5 focus:border-snapp-purple/30 rounded-[1.5rem] pl-12 pr-4 py-3.5 text-sm text-white placeholder:text-gray-600 outline-none transition-all shadow-xl font-medium"
                />
              </div>
              <div className="sm:w-48">
                <Select value={filterPriority} onValueChange={setFilterPriority}>
                  <SelectTrigger className="w-full bg-[#1a1a1b] border border-white/5 rounded-[1.5rem] py-3.5 h-auto text-sm text-white font-medium focus:ring-0">
                    <SelectValue placeholder={t('announcements.filterPriority')} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1b] border-white/10 text-white">
                    <SelectItem value="all">{t('announcements.priorities.all')}</SelectItem>
                    <SelectItem value="urgent">🔴 {t('announcements.priorities.urgent')}</SelectItem>
                    <SelectItem value="high">🟠 {t('announcements.priorities.high')}</SelectItem>
                    <SelectItem value="normal">🔵 {t('announcements.priorities.normal')}</SelectItem>
                    <SelectItem value="low">⚪ {t('announcements.priorities.low')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-10 h-10 border-2 border-snapp-blue border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredAnnouncements.map((ann) => {
                const priority = priorityConfig[ann.priority] || priorityConfig.normal;
                const PriorityIcon = priority.icon;

                return (
                  <div
                    key={ann.id}
                    className={cn(
                      "bg-[#1a1a1b]/60 backdrop-blur-xl border border-white/5 rounded-[2rem] p-6 shadow-xl relative overflow-hidden group transition-all duration-300",
                      ann.is_pinned && "border-l-4 border-l-amber-500"
                    )}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-3">
                        <div className="flex flex-wrap items-center gap-2">
                          {ann.is_pinned && (
                            <div className="p-1.5 bg-amber-500/10 rounded-lg">
                              <Pin className="w-4 h-4 text-amber-500" />
                            </div>
                          )}
                          <h3 className="text-xl font-black text-white tracking-tight leading-tight">{ann.title}</h3>
                          <div className={cn("px-2.5 py-1 rounded-full text-[9px] font-black tracking-widest uppercase flex items-center gap-1.5", priority.color.replace('bg-', 'bg-opacity-20 text-').split(' ')[0], priority.color)}>
                            <PriorityIcon className="w-3 h-3" />
                            {priority.label}
                          </div>
                          {ann.target_unit && ann.target_unit !== 'all' && (
                            <div className="px-2.5 py-1 bg-snapp-blue/10 text-snapp-blue rounded-full text-[9px] font-black tracking-widest uppercase flex items-center gap-1.5">
                              <Users className="w-3 h-3" />
                              {ann.target_unit}
                            </div>
                          )}
                        </div>

                        {ann.body && (
                          <p className="text-gray-400 text-sm font-medium leading-relaxed max-w-2xl">{ann.body}</p>
                        )}

                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pt-2">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-[10px] font-black text-gray-400">
                              {ann.created_by_name?.substring(0, 1) || 'A'}
                            </div>
                            <span className="text-[11px] font-black text-gray-500 uppercase tracking-widest">{ann.created_by_name}</span>
                          </div>
                          <span className="text-[11px] font-black text-gray-700">•</span>
                          <span className="text-[11px] font-black text-gray-500 uppercase tracking-widest">{new Date(ann.created_at).toLocaleDateString()}</span>
                          {ann.due_at && (
                            <div className="flex items-center gap-1.5 bg-amber-500/5 px-2.5 py-1 rounded-full">
                              <Calendar className="w-3.5 h-3.5 text-amber-500" />
                              <span className="text-[10px] font-black text-amber-500 uppercase tracking-widest">Due: {new Date(ann.due_at).toLocaleDateString()}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {isCoach && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openEditModal(ann)}
                            className="p-3 bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white rounded-2xl transition-all active:scale-90"
                          >
                            <Pencil className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(ann.id)}
                            className="p-3 bg-red-500/10 hover:bg-red-500 text-red-500 hover:text-white rounded-2xl transition-all active:scale-90"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {!loading && filteredAnnouncements.length === 0 && (
                <div className="text-center py-20 bg-[#1a1a1b]/30 rounded-[2.5rem] border border-white/5 border-dashed">
                  <Bell className="w-16 h-16 text-white/5 mx-auto mb-4" />
                  <p className="text-gray-500 font-bold uppercase tracking-widest text-xs">{t('announcements.noAnnouncements')}</p>
                  {isCoach && (
                    <button
                      onClick={openCreateModal}
                      className="mt-6 bg-snapp-blue hover:bg-snapp-blue/90 text-white px-8 py-3 rounded-2xl font-black text-xs uppercase tracking-wider transition-all active:scale-95 shadow-lg shadow-blue-500/20"
                    >
                      {t('announcements.createFirst')}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Floating Action Button for Mobile Coaches */}
        {isCoach && (
          <button
            onClick={openCreateModal}
            className="lg:hidden fixed bottom-28 right-6 w-14 h-14 bg-snapp-blue text-white rounded-2xl shadow-2xl flex items-center justify-center transition-all active:scale-90 z-[101]"
          >
            <Plus className="w-8 h-8" />
          </button>
        )}

        {/* Create Form Modal - Modern Layout */}
        {showCreateForm && (
          <div className="fixed inset-0 bg-[#0a0a0b]/90 z-[200] flex items-end sm:items-center justify-center p-0 sm:p-4 backdrop-blur-xl animate-in fade-in duration-300">
            <div className="bg-[#1a1a1b] border-t sm:border border-white/10 w-full max-w-lg shadow-2xl rounded-t-[2.5rem] sm:rounded-[2.5rem] overflow-hidden animate-in slide-in-from-bottom-20 duration-500">
              <div className="p-8 space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-2xl font-black text-white uppercase tracking-tight">
                    {editingAnnouncement ? "Edit Update" : "New Update"}
                  </h3>
                  <button onClick={() => setShowCreateForm(false)} className="p-2 hover:bg-white/10 rounded-full transition-colors">
                    <Trash2 className="w-6 h-6 text-gray-500" />
                  </button>
                </div>

                <form onSubmit={handleSave} className="space-y-6">
                  <div className="space-y-2">
                    <Label className="text-[10px] font-black text-gray-500 uppercase tracking-widest px-1">{t('announcements.formTitle')} *</Label>
                    <input
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder={t('announcements.placeholderTitle')}
                      className="w-full bg-white/5 border border-white/5 rounded-2xl px-5 py-4 text-white placeholder:text-gray-600 focus:border-snapp-blue/30 outline-none transition-all font-medium"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[10px] font-black text-gray-500 uppercase tracking-widest px-1">{t('announcements.formDetails')}</Label>
                    <textarea
                      value={formData.body}
                      onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                      placeholder={t('announcements.placeholderDetails')}
                      className="w-full bg-white/5 border border-white/5 rounded-2xl px-5 py-4 text-white placeholder:text-gray-600 focus:border-snapp-blue/30 outline-none transition-all font-medium min-h-[120px] resize-none"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[10px] font-black text-gray-500 uppercase tracking-widest px-1">{t('announcements.priority')}</Label>
                      <Select
                        value={formData.priority}
                        onValueChange={(v) => setFormData({ ...formData, priority: v })}
                      >
                        <SelectTrigger className="bg-white/5 border-white/5 rounded-2xl h-14 text-white focus:ring-0">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1a1a1b] border-white/10 text-white">
                          <SelectItem value="low">{t('announcements.priorities.low')}</SelectItem>
                          <SelectItem value="normal">{t('announcements.priorities.normal')}</SelectItem>
                          <SelectItem value="high">{t('announcements.priorities.high')}</SelectItem>
                          <SelectItem value="urgent">{t('announcements.priorities.urgent')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-[10px] font-black text-gray-500 uppercase tracking-widest px-1">{t('announcements.target')}</Label>
                      <Select
                        value={formData.target_unit}
                        onValueChange={(v) => setFormData({ ...formData, target_unit: v })}
                      >
                        <SelectTrigger className="bg-white/5 border-white/5 rounded-2xl h-14 text-white focus:ring-0">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1a1a1b] border-white/10 text-white">
                          <SelectItem value="all">{t('announcements.targetAll')}</SelectItem>
                          <SelectItem value="offense">{t('announcements.targetOffense')}</SelectItem>
                          <SelectItem value="defense">{t('announcements.targetDefense')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[10px] font-black text-gray-500 uppercase tracking-widest px-1">Target Date</Label>
                    <input
                      type="date"
                      value={formData.due_at}
                      onChange={(e) => setFormData({ ...formData, due_at: e.target.value })}
                      className="w-full bg-white/5 border border-white/5 rounded-2xl px-5 py-4 text-white outline-none transition-all font-medium [color-scheme:dark]"
                    />
                  </div>

                  <div className="flex gap-6 px-1">
                    <label className="flex items-center gap-3 cursor-pointer group">
                      <div className="relative w-10 h-6">
                        <input
                          type="checkbox"
                          checked={formData.is_pinned}
                          onChange={(e) => setFormData({ ...formData, is_pinned: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-10 h-6 bg-white/5 rounded-full peer peer-checked:bg-amber-500 transition-colors border border-white/10"></div>
                        <div className="absolute left-1 top-1 w-4 h-4 bg-gray-400 peer-checked:bg-white peer-checked:translate-x-4 rounded-full transition-all"></div>
                      </div>
                      <span className="text-xs font-black text-gray-400 uppercase tracking-widest group-hover:text-white transition-colors">{t('announcements.pin')}</span>
                    </label>

                    <label className="flex items-center gap-3 cursor-pointer group">
                      <div className="relative w-10 h-6">
                        <input
                          type="checkbox"
                          checked={formData.show_in_calendar}
                          onChange={(e) => setFormData({ ...formData, show_in_calendar: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-10 h-6 bg-white/5 rounded-full peer peer-checked:bg-snapp-blue transition-colors border border-white/10"></div>
                        <div className="absolute left-1 top-1 w-4 h-4 bg-gray-400 peer-checked:bg-white peer-checked:translate-x-4 rounded-full transition-all"></div>
                      </div>
                      <span className="text-xs font-black text-gray-400 uppercase tracking-widest group-hover:text-white transition-colors">{t('announcements.showInCalendar')}</span>
                    </label>
                  </div>

                  <div className="pt-4 flex flex-col gap-3">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-snapp-blue hover:bg-snapp-blue/90 disabled:opacity-50 text-white h-14 rounded-2xl font-black uppercase tracking-[0.1em] transition-all active:scale-95 shadow-xl shadow-blue-500/20 flex items-center justify-center gap-3"
                    >
                      {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
                      {editingAnnouncement ? 'Save Changes' : (t('common.create') || 'Share Update')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowCreateForm(false)}
                      className="w-full bg-white/5 hover:bg-white/10 text-gray-400 h-14 rounded-2xl font-black uppercase tracking-[0.1em] transition-all active:scale-95"
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </TeamGate >
  );
};

export default Notes;
